/**
 * Admissions CRM — Readiness Queue API contract (plan #001, U5).
 *
 * GET /v1/enrollment/queue
 *
 * Admin-only, school-scoped read of the admissions readiness queue: Active
 * candidates for a selected entering year, ranked fewest-blockers-first then
 * stalest-first (R7, R8, KTD12). Each row carries its blocking-step count,
 * stage, entering grade, "ready to advance" flag, last-activity timestamp, and
 * the family "has app account" badge (KTD7). The response also carries the
 * stage CHIPS (catalog snapshot joined to per-stage counts by `statusId` —
 * never by description, KTD11), the derived enrollment `state`, `lastSynced`,
 * and the list of available entering years for the picker.
 *
 * School is the active-school request context (`x-school-id`), never a
 * caller-supplied id — cross-school access is denied by construction (R14).
 */

import { z } from 'zod'

import { EnrollmentStateSchema } from './reconciliation'

/**
 * One stage chip: the catalog `statusId` + its snapshot label + the live count
 * of queue-scoped candidates currently in that stage. The label is the
 * HISTORICAL snapshot (KTD11) — chips join `statusId` to the catalog, never the
 * description, so a mid-season rename can't split a stage's count.
 */
export const EnrollmentStageChipSchema = z.object({
  /** BB candidate-status (stage) id — the stable join key. */
  statusId: z.number().int(),
  /** Snapshot label for the stage (e.g. "Applied"), or null when unlabeled. */
  description: z.string().nullable(),
  /** Candidates in this stage within the current (year + Active/Inactive) scope. */
  count: z.number().int().nonnegative()
})
export type EnrollmentStageChip = z.infer<typeof EnrollmentStageChipSchema>

/** One row in the readiness queue (PII present — staff-gated surface). */
export const EnrollmentQueueRowSchema = z.object({
  /** Candidate row publicId — addresses the detail endpoint. */
  publicId: z.string(),
  /** BB candidate id (== UsersOnSchools.platform_id when rostered). */
  candidateId: z.string(),
  /**
   * The candidate's school-user publicId (`UsersOnSchools.publicId`) — the key
   * the User Details modal opens on. Resolved by the roster join on
   * `(schoolId, platform_id == candidateId)` WITHOUT a linked-login filter.
   * Null only when no roster row exists (defensive; shouldn't occur).
   */
  schoolUserPublicId: z.string().nullable(),
  /** Candidate first name, or null. */
  firstName: z.string().nullable(),
  /** Candidate last name, or null. */
  lastName: z.string().nullable(),
  /** Pipeline stage id (BB `status` object), or null. */
  statusId: z.number().int().nullable(),
  /** Snapshot stage label joined from the catalog (KTD11), or null. */
  statusDescription: z.string().nullable(),
  /** Entering grade label, or null. */
  enteringGrade: z.string().nullable(),
  /** Count of required, still-`Awaiting` checklist steps (the blockers). */
  blockerCount: z.number().int().nonnegative(),
  /** True when `blockerCount === 0` — the candidate is ready to advance (R7). */
  readyToAdvance: z.boolean(),
  /** Last activity timestamp (ISO 8601) via the KTD4 fallback chain, or null. */
  lastActivityAt: z.string().nullable(),
  /**
   * True when a PARENT-CLASS relative (KTD7) has a roster row with a linked
   * login — the "family has app account" badge. Grandparent/Sibling never
   * drive this.
   */
  familyHasAppAccount: z.boolean()
})
export type EnrollmentQueueRow = z.infer<typeof EnrollmentQueueRowSchema>

export const GetEnrollmentQueueResponseSchema = z.object({
  /** The school's derived enrollment surface state (R6 — BROKEN never reads empty). */
  state: EnrollmentStateSchema,
  /** Last successful ENROLLMENTS sync (ISO 8601), or null if never. */
  lastSynced: z.string().nullable(),
  /** The entering year this queue is scoped to (echoed for the picker). */
  enteringYear: z.string(),
  /** Distinct entering-year labels present in the mirror (for the year picker). */
  availableYears: z.array(z.string()),
  /** Whether the Inactive filter was applied (Inactive included when true). */
  includeInactive: z.boolean(),
  /** Stage chips: catalog snapshot joined to per-stage counts (KTD11). */
  chips: z.array(EnrollmentStageChipSchema),
  /**
   * True when the row LIST was capped server-side (bounded-work pattern,
   * KTD12). The chip counts remain exact across the full scope.
   */
  isTruncated: z.boolean(),
  /** The ranked queue rows (capped server-side; chips carry the exact totals). */
  rows: z.array(EnrollmentQueueRowSchema)
})
export type GetEnrollmentQueueResponse = z.infer<typeof GetEnrollmentQueueResponseSchema>
